import { getGetShearAnchorStoreySelectionQueryKey } from '@api/project-shear-connections/project-shear-connections'
import { getGetShearAnchorChecksQueryKey } from '@api/shear-anchor-checks/shear-anchor-checks'
import { useQueryClient } from '@tanstack/react-query'
import { useResultsStore } from '@editorStores'
import {
  getDesignPositionBundle,
  getElementChecksBundle,
  getLoadsPerElement,
  getMemberCheckSettings,
  getAnchorChecks,
  getDesignForces,
  getAllCharacteristicMemberSupportReactions,
} from '@queries'
import useInnerForcesEnvelopeInvalidation from './useInnerForcesEnvelopeInvalidation'

/**
 * Drop the computed results of a project, so the user has to recalculate.
 *
 * Invalidating the result queries is not enough on its own. The results shown in the
 * UI are read from the results store, which `useResultsQueries` fills from those
 * queries. Once the backend has reset the results, its result GETs answer 409, which
 * the request interceptor turns into `data: null` -- and the result queries skip
 * their store writes on null (and are partly disabled once their parent query is
 * null too). Without clearing the store here, stale checks and utilisations stay on
 * screen until the page is reloaded.
 */
const useResultsInvalidation = () => {
  const queryClient = useQueryClient()
  const clearChecks = useResultsStore(state => state.clearForCheckReRun)
  const invalidateInnerForcesEnvelopes = useInnerForcesEnvelopeInvalidation()

  return async (projectId: string) => {
    clearChecks()

    await Promise.all([
      queryClient.invalidateQueries(getDesignPositionBundle.getKey(projectId)),
      queryClient.invalidateQueries(getLoadsPerElement.getKey(projectId)),
      queryClient.invalidateQueries(getMemberCheckSettings.getKey(projectId)),
      queryClient.invalidateQueries(getElementChecksBundle.getKey(projectId)),
      queryClient.invalidateQueries(getAnchorChecks.getKey(projectId)),
      // The shear anchor checks live in an orval-generated query, not the
      // results store; with staleTime Infinity they would otherwise keep the
      // first fetch until a reload (the backend recomputes lazily on GET).
      queryClient.invalidateQueries(getGetShearAnchorChecksQueryKey(projectId)),
      // The backend normalises the per-storey anchor selection when the shear
      // library or prioritization changes (all of which invalidate results
      // here); without this the panel would build its next save on a stale
      // base and resurrect pruned exclusions.
      queryClient.invalidateQueries(getGetShearAnchorStoreySelectionQueryKey(projectId)),
      queryClient.invalidateQueries(getDesignForces.getKey(projectId)),
      queryClient.invalidateQueries(getAllCharacteristicMemberSupportReactions.getKey(projectId)),
      invalidateInnerForcesEnvelopes(projectId),
    ])
  }
}

export default useResultsInvalidation
